import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

import { useAuth } from "@/hooks/use-auth";
import {
  Menu,
  X,
  ChevronDown,
  Facebook,
} from "lucide-react";
import { SearchBar } from "@/components/search-bar";
import type { NavItem } from "@shared/schema";
import { isPublicLinkValid } from "@shared/cms-routing";
import mtcLogo from "@/assets/mtc-logo-header.webp";
import mtcLogoFooter from "@/assets/mtc-logo-footer.webp";

type NavLink = { title: string; href: string; openInNewTab: boolean; sublinks: NavLink[] };
type NavGroup = { id: string; label: string; href: string; openInNewTab: boolean; links: NavLink[] };
type CmsPageLink = { slug: string; isPublished?: boolean | null };

function buildNavTree(items: NavItem[], isLinkValid: (url: string) => boolean): NavGroup[] {
  const childrenOf = (parentId: string): NavLink[] =>
    items
      .filter((c) => c.parentId === parentId)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((c) => {
        const sublinks = childrenOf(c.id);
        const href = c.url && isLinkValid(c.url) ? c.url : "";
        return {
          title: c.label,
          href,
          openInNewTab: c.openInNewTab ?? false,
          sublinks,
          // Keep a parent heading when it still contains valid children, but
          // omit a stale CMS leaf rather than rendering an empty menu item.
          hasValidLink: !c.url || c.url === "#" || Boolean(href) || sublinks.length > 0,
        };
      })
      .filter((c) => c.hasValidLink)
      .map(({ hasValidLink: _hasValidLink, ...link }) => link);
  const parents = items
    .filter((i) => !i.parentId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return parents
    .map((p) => {
      const links = childrenOf(p.id);
      const href = p.url && isLinkValid(p.url) ? p.url : "";
      return {
        id: p.id,
        label: p.label,
        href,
        openInNewTab: p.openInNewTab ?? false,
        links,
        // A stale parent can remain as a heading for valid children, but
        // should not become an empty dropdown after its URL is removed.
        hasValidLink: !p.url || p.url === "#" || Boolean(href) || links.length > 0,
      };
    })
    .filter((p) => p.hasValidLink)
    .map(({ hasValidLink: _hasValidLink, ...group }) => group);
}

function NavLinkItem({ link, currentPath, className, onClick }: { link: NavLink; currentPath: string; className: string; onClick?: () => void }) {
  const testId = `nav-link-${(link.href || link.title).replace(/[^a-zA-Z0-9]/g, '-').replace(/^-+/, '')}`;
  if (link.openInNewTab && link.href) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} data-testid={testId}>
        {link.title}
      </a>
    );
  }
  // Items with no real link (blank or "#") act as plain labels, e.g. a
  // sub-menu heading that just groups the links beneath it.
  if (!link.href || link.href === "#") {
    return (
      <span className={`${className} cursor-default`} data-testid={testId}>
        {link.title}
      </span>
    );
  }
  return (
    <Link href={link.href}>
      <span className={className} onClick={onClick} data-testid={testId}>
        {link.title}
      </span>
    </Link>
  );
}

function NavDropdown({ group, currentPath }: { group: NavGroup; currentPath: string }) {
  const [open, setOpen] = useState(false);
  const isActive = group.links.some(l => currentPath === l.href || (l.href && currentPath.startsWith(l.href + "/")));

  // A parent with its own URL and no children acts as a direct link.
  if (group.links.length === 0 && group.href) {
    const directClass = `flex items-center gap-1 text-sm font-medium cursor-pointer transition-colors ${currentPath === group.href ? "text-primary font-semibold" : "text-primary hover:text-primary/80"}`;
    const testId = `nav-dropdown-${group.label.toLowerCase().replace(/\s+/g, '-')}`;
    if (group.openInNewTab) {
      return (
        <a href={group.href} target="_blank" rel="noopener noreferrer" className={directClass} data-testid={testId}>
          {group.label}
        </a>
      );
    }
    return (
      <Link href={group.href}>
        <span className={directClass} data-testid={testId}>
          {group.label}
        </span>
      </Link>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={`flex items-center gap-1 text-sm font-medium transition-colors ${isActive ? "text-primary font-semibold" : "text-primary hover:text-primary/80"}`}
        data-testid={`nav-dropdown-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {group.label}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className="bg-popover border border-border rounded-md shadow-lg py-1 min-w-[260px]">
            {group.links.map(link => (
              <div key={link.href + link.title}>
                <NavLinkItem
                  link={link}
                  currentPath={currentPath}
                  className={`block px-4 py-2 text-sm cursor-pointer hover:bg-muted ${currentPath === link.href ? "text-primary font-semibold bg-muted" : "text-primary"} ${link.sublinks.length > 0 ? "font-medium" : ""}`}
                />
                {link.sublinks.map(sub => (
                  <NavLinkItem
                    key={sub.href + sub.title}
                    link={sub}
                    currentPath={currentPath}
                    className={`block pl-8 pr-4 py-1.5 text-sm cursor-pointer hover:bg-muted ${currentPath === sub.href ? "text-primary font-semibold bg-muted" : "text-primary/80"}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileSection({ group, currentPath, onClose }: { group: NavGroup; currentPath: string; onClose: () => void }) {
  if (group.links.length === 0 && group.href) {
    if (group.openInNewTab) {
      return (
        <a href={group.href} target="_blank" rel="noopener noreferrer" className="block py-2 text-sm font-medium cursor-pointer" onClick={onClose}>
          {group.label}
        </a>
      );
    }
    return (
      <Link href={group.href}>
        <span className="block py-2 text-sm font-medium cursor-pointer" onClick={onClose}>{group.label}</span>
      </Link>
    );
  }
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{group.label}</div>
      {group.links.map(l => (
        <div key={l.href + l.title}>
          <NavLinkItem
            link={l}
            currentPath={currentPath}
            className="block py-1.5 pl-3 text-sm cursor-pointer"
            onClick={onClose}
          />
          {l.sublinks.map(sub => (
            <NavLinkItem
              key={sub.href + sub.title}
              link={sub}
              currentPath={currentPath}
              className="block py-1.5 pl-8 text-sm text-muted-foreground cursor-pointer"
              onClick={onClose}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function PublicLayout({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const showDesignCredit = !/^\/(?:member|admin)(?:\/|$)/.test(location);
  const { user, logout, isLoggingOut } = useAuth();

  const { data: cmsPages } = useQuery<CmsPageLink[]>({
    queryKey: ["/api/cms"],
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { data: navItems = [] } = useQuery<NavItem[]>({
    queryKey: ["/api/nav"],
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { data: footerData } = useQuery<Record<string, any>>({
    queryKey: ["/api/footer"],
    staleTime: 0,
    refetchOnMount: "always",
  });
  const origin = window.location.origin;
  const isLinkValid = (url: string) => isPublicLinkValid(url, cmsPages ?? [], origin);
  const defaultQuickLinks: { label: string; url: string }[] = [
    { label: "Home", url: "/" },
    { label: "Education & Events", url: "" },
    { label: "Membership", url: "/membership" },
    { label: "Committees", url: "/about/committees" },
    { label: "Prevention", url: "" },
    { label: "Legislative", url: "" },
    { label: "Resources", url: "" },
    { label: "About", url: "" },
    { label: "Join MTC", url: "/membership" },
    { label: "MTC Member Hospitals", url: "/directory?tab=institutions" },
    { label: "MTC General Membership Meetings", url: "/membership/general-meeting" },
    { label: "MTC Conference Registration", url: "/education/regional-conferences" },
    { label: "MTC Scholarships", url: "" },
    { label: "Networking Directory", url: "/directory?tab=members" },
  ];
  const configuredQuickLinks: { label: string; url: string }[] =
    Array.isArray(footerData?.quickLinks) && footerData.quickLinks.length > 0
      ? footerData.quickLinks
      : defaultQuickLinks;
  const quickLinks = configuredQuickLinks.filter((link) => !link.url || isLinkValid(link.url));
  const privacyUrl = isLinkValid("/privacy") ? "/privacy" : "";
  const newsRequestUrl = footerData?.newsRequestUrl || "/news-request";
  const validNewsRequestUrl = isLinkValid(newsRequestUrl) ? newsRequestUrl : "";
  const facebookUrl = footerData?.facebookUrl || "https://www.facebook.com/michigantraumacoalition#";
  const validFacebookUrl = isLinkValid(facebookUrl)
    ? facebookUrl
    : "";
  const footer = {
    tagline: footerData?.tagline || "Advancing trauma care across Michigan since 1991.",
    mailingAddress: footerData?.mailingAddress || "2581 Grand River Dr NE\nGrand Rapids, MI 49525",
    businessAddress: footerData?.businessAddress || "2843 E. Grand River, #254\nEast Lansing, MI 48823",
    email: footerData?.email || "administrator@mitrauma.org",
    phone: footerData?.phone || "616-259-0034",
    facebookUrl: validFacebookUrl,
    newsRequestLabel: footerData?.newsRequestLabel || "Submit MTC news items, posts, and press releases",
    newsRequestUrl: validNewsRequestUrl,
  };
  const navGroups = buildNavTree(navItems, isLinkValid);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <div className="public-site min-h-screen bg-background flex flex-col">
      <nav className="sticky top-0 z-50 border-b border-border/50 backdrop-blur-md bg-background/80">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link href="/">
            <span className="flex items-center cursor-pointer" data-testid="nav-logo">
              <img src={mtcLogo} alt="Michigan Trauma Coalition" width={250} height={114} decoding="async" {...{ fetchpriority: "high" }} className="h-[46px] w-auto" />
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            {navGroups.map((group) => (
              <NavDropdown key={group.id} group={group} currentPath={location} />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <SearchBar />
            {user ? (
              <>
                <Link href={user.isAdmin ? "/admin" : "/member"}>
                  <Button variant="ghost" data-testid="button-dashboard">
                    {user.isAdmin ? "Admin Dashboard" : "Member Portal"}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={() => logout()}
                  disabled={isLoggingOut}
                  data-testid="button-public-logout"
                >
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Link href={`/auth/login?returnTo=${encodeURIComponent(location)}`}>
                  <Button variant="ghost" data-testid="button-signin">
                    Login
                  </Button>
                </Link>
                <Link href="/membership">
                  <Button data-testid="button-join">Join MTC</Button>
                </Link>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-4 max-h-[80vh] overflow-y-auto">
            <Link href="/">
              <span className="block py-2 text-sm font-medium cursor-pointer" onClick={closeMobile} data-testid="mobile-link-home">Home</span>
            </Link>
            {navGroups.map((group) => (
              <MobileSection key={group.id} group={group} currentPath={location} onClose={closeMobile} />
            ))}
          </div>
        )}
      </nav>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <img src={mtcLogoFooter} alt="Michigan Trauma Coalition" width={560} height={264} loading="lazy" decoding="async" className="w-3/4 h-auto mb-4" />
              <p className="text-left text-sm text-primary-foreground/70 mb-4">
                {footer.tagline}
              </p>
              <div className="text-sm text-primary-foreground/70 mb-4" data-testid="footer-mailing-address">
                <p className="font-bold text-primary-foreground">Mailing Address:</p>
                {footer.mailingAddress.split("\n").map((line: string, i: number) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
              <div className="text-sm text-primary-foreground/70 mb-4" data-testid="footer-business-address">
                <p className="font-bold text-primary-foreground">Business Address:</p>
                {footer.businessAddress.split("\n").map((line: string, i: number) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-semibold mb-3 text-lg bg-secondary text-secondary-foreground rounded-md px-4 py-3 block w-full text-left">Contact</h2>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li>
                  <span className="font-bold text-primary-foreground">Email: </span>
                  <a href={`mailto:${footer.email}`} className="hover:text-primary-foreground" data-testid="footer-email">
                    {footer.email}
                  </a>
                </li>
                <li>
                  <span className="font-bold text-primary-foreground">Phone: </span>
                  <a href={`tel:${footer.phone.replace(/[^+\d]/g, "")}`} className="hover:text-primary-foreground" data-testid="footer-phone">
                    {footer.phone}
                  </a>
                </li>
                <li className="mt-12">
                  {footer.newsRequestUrl ? (
                    <a href={footer.newsRequestUrl} className="font-bold hover:text-primary-foreground" data-testid="footer-news-request">
                      {footer.newsRequestLabel}
                    </a>
                  ) : (
                    <span className="font-bold" data-testid="footer-news-request">
                      {footer.newsRequestLabel}
                    </span>
                  )}
                </li>
              </ul>
            </div>
            <div>
              <h2 className="font-semibold mb-3 text-lg bg-secondary text-secondary-foreground rounded-md px-4 py-3 block w-full text-left">Quick Links</h2>
              <ul className="space-y-2" data-testid="footer-quick-links">
                {quickLinks.map((link, i) => (
                  <li key={i}>
                    {!link.url ? (
                      <span className="text-sm text-primary-foreground/70">{link.label}</span>
                    ) : link.url.startsWith("/") ? (
                      <Link href={link.url}>
                        <span className="text-sm text-primary-foreground/70 hover:text-primary-foreground cursor-pointer">{link.label}</span>
                      </Link>
                    ) : (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-foreground/70 hover:text-primary-foreground">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-primary-foreground/20 text-sm text-primary-foreground/60">
            <div className="overflow-x-auto">
            <p className="w-max min-w-full whitespace-nowrap text-center">
              Copyright &copy; {new Date().getFullYear()} Michigan Trauma Coalition. All rights reserved.{" "}
              {privacyUrl && (
                <Link href={privacyUrl}>
                  <span className="hover:text-primary-foreground cursor-pointer" data-testid="footer-privacy">Privacy Notice.</span>
                </Link>
              )}{" "}
              <Link href="/sitemap">
                <span className="hover:text-primary-foreground cursor-pointer" data-testid="footer-sitemap">Sitemap.</span>
              </Link>
              {showDesignCredit && (
              <>{" "}Website Design by:{" "}
              <a
                href="https://rebeccavandenberg.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground underline underline-offset-2"
              >
                VanDenBerg Web &amp; Creative
              </a>
              </>
              )}
            </p>
            </div>
            {footer.facebookUrl && (
              <span className="mt-4 flex items-center justify-center gap-2">
                Follow us:
                <a
                  href={footer.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-foreground"
                  aria-label="Follow us on Facebook"
                  data-testid="link-facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              </span>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
